import React, { useCallback, useMemo, useState } from 'react';
import type { RepeatRowsPayload, DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp';
import { ContextMenu, ContextMenuBuilder } from '@/components/custom/erp/input-table-v2';
import { type AccountConstraint, ChiTietHoaDonBanHangDichVu } from '@/types/schemas';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { getDetailTableColumns } from '../../../cols-definition';
import type { SelectedCellInfo } from './useDetailRows';
import { copyCellValue } from '@/lib/table-handlers';
import { useDetailPaste } from '../../../hooks';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  rows: ChiTietHoaDonBanHangDichVu[];
  selectedRowUuid: string | null;
  selectedCell?: SelectedCellInfo | null;
  handleRowClick: DataTableRowEventHandler<ChiTietHoaDonBanHangDichVu>;
  onCellClick?: (params: DataTableRenderCellParams<ChiTietHoaDonBanHangDichVu>) => void;
  handleAddRow: () => void;
  handleDeleteRow: () => void;
  handleCopyRow: () => void;
  handlePasteRow: (clipboardText?: string) => void | Promise<void>;
  handleMoveRow: (direction: 'up' | 'down') => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  handleCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  formMode: FormMode;
  dienGiai: string;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'] as const;
const noop = () => {};
const detail = (row: BanHangSearchParams) => constraintValues(row);

export default function DetailTab({
  rows,
  selectedRowUuid,
  selectedCell,
  handleRowClick,
  onCellClick,
  handleAddRow,
  handleDeleteRow,
  handleCopyRow,
  handlePasteRow,
  handleMoveRow,
  onRepeatForRows,
  handleCellValueChange,
  formMode,
  dienGiai,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  constraints = []
}: DetailTabProps) {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: BanHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });
  const columns = useMemo(
    () => getDetailTableColumns(formMode, handleCellValueChange, dienGiai, cellError) as BanHangRecord[],
    [cellError, dienGiai, formMode, handleCellValueChange]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues,
            fallback: text => {
              console.log('Copy data per column:', text);
            }
          });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );
  const tableActions = useMemo(
    () => ({
      add: handleAddRow,
      delete: handleDeleteRow,
      copy: handleCopyRow,
      paste: handlePasteRow,
      moveUp: () => handleMoveRow('up'),
      moveDown: () => handleMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [handleAddRow, handleCopyRow, handleDeleteRow, handleMoveRow, handlePasteRow]
  );
  const handleRowId = useCallback((row: ChiTietHoaDonBanHangDichVu) => row?.uuid || '', []);
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams<ChiTietHoaDonBanHangDichVu>) => {
      onCellClick?.(params);

      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );

  return (
    <>
      <ErpInputTableV2<ChiTietHoaDonBanHangDichVu>
        rows={rows}
        autoHeight
        showIndexColumn
        bottomSelector='[data-arito-bottom-bar]'
        bottomOffset={32}
        onRowClick={handleRowClick}
        onCellClick={onCellClick}
        onCellContextMenu={handleCellContextMenu}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={handleRowId}
        pasteTarget={selectedCell}
        actionsDisabled={formMode === 'view'}
        actionOrder={DETAIL_ACTION_ORDER as BanHangRecord}
        actions={tableActions}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() => {
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          });
        }}
      />
    </>
  );
}
